import {
  ArrowFlightSQLAppInfoResponse,
  DefaultApi,
  DeleteArrowFlightSqlAppInfoRequest,
} from "@coeff/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  AlertExclamation,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Checkbox,
  Form,
  ImportTypeIcon,
  Input,
  LoaderWithPerfTimings,
  Modal,
  OAuthActions,
  OAuthConfiguredAlert,
  OAuthDeleteButton,
  SettingsLayout,
  Typography,
} from "../components";
import { COLORS } from "../constants";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils";

import { Header } from "./Dashboard/components";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
`;

const Container = styled.div`
  padding: 25px 30px 48px;
  max-width: 660px;
`;

const StyledHeader = styled(Header)`
  h1 {
    margin: 0 10px;
  }
`;

const StyledAlert = styled(Alert)`
  line-height: 1.8em;
  padding: 12px;

  code {
    white-space: nowrap;
    background-color: ${COLORS.black4};
    padding: 4px 6px;
  }

  .ant-alert-icon {
    margin: 6px;
  }
`;

const FormGroup = styled.div`
  label {
    font-weight: bold;
    margin: 20px 12px 8px 2px;
  }
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 40px -15px -10px -15px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 20px;
`;

const CallHeaderLabel = styled.div`
  font-weight: bold;
  margin: 20px 12px 8px 2px;
`;

const fetchArrowFlightSqlAppInfoList = async (api: DefaultApi) => {
  const response = await api.listArrowFlightSqlAppInfo();
  return response?.data?.apps_list;
};

export const ArrowFlightSqlOAuthManageSettings = () => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const track = useTrack();

  const history = useHistory();

  const { clientId } = useParams<{ clientId: string }>();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);

  const [_, contextHolder] = message.useMessage();

  const { apiClient: api } = useApiContext();

  const queryClient = useQueryClient();

  const {
    data: appInfo,
    isFetching: isFetchingAppInfo,
    error: errorFetchingAppInfo,
  } = useQuery(
    [QueryCacheKey.ARROW_FLIGHT_SQL_APP_INFO, clientId],
    () => fetchArrowFlightSqlAppInfoList(api),
    {
      select: (appsList): ArrowFlightSQLAppInfoResponse | undefined =>
        appsList?.find(app => app.client_id === clientId),
    }
  );

  const deleteArrowFlightSqlAppInfo = async (
    api: DefaultApi,
    deleteRequest: DeleteArrowFlightSqlAppInfoRequest
  ) => {
    await api.deleteArrowFlightSqlAppInfo({
      delete_arrow_flight_sql_app_info_request: deleteRequest,
    });
  };

  const deleteAppInfoMutation = useMutation(
    (info: ArrowFlightSQLAppInfoResponse) =>
      deleteArrowFlightSqlAppInfo(api, {
        client_id: info.client_id ?? "",
        domain: info.domain,
      }),
    {
      onSuccess: () => {
        setShowDeleteModal(false);
        history.goBack();
        message.success("Arrow Flight SQL OAuth configuration deleted");
        queryClient.invalidateQueries([QueryCacheKey.ARROW_FLIGHT_SQL_APP_INFO]);
      },
      onError: () => message.error("Something went wrong. Please try again."),
    }
  );

  React.useEffect(() => {
    document.title = "Arrow Flight SQL OAuth Settings - Coefficient Workspace";
    track("workspace_arrow_flight_sql_oauth_settings_viewed");
  }, []);

  const renderContent = (children: React.ReactNode) => {
    return (
      <Container>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/admin` }}>Admin</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/admin/settings` }}>Settings</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/settings/oauth-settings` }}>OAuth Settings</Link>
          </BreadcrumbItem>
        </Breadcrumb>
        <StyledHeader>
          <ImportTypeIcon type="arrow_flight_sql" />
          <h1>Arrow Flight SQL OAuth</h1>
        </StyledHeader>
        {contextHolder}
        {children}
      </Container>
    );
  };

  if (clientFlags?.is_gmail_like_domain) {
    return (
      <SettingsLayout dataSourceType="arrow_flight_sql" title="Arrow Flight SQL OAuth Settings">
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message="Arrow Flight SQL OAuth is not supported for your domain"
          description={`Arrow Flight SQL OAuth is not supported for ${
            clientFlags.email.split("@")[1]
          }. Please use a different account to configure Arrow Flight SQL OAuth.`}
        />
      </SettingsLayout>
    );
  }

  if (deleteAppInfoMutation.isLoading || isFetchingAppInfo) {
    return renderContent(<LoaderWithPerfTimings name="ArrowFlightSqlOAuthManageSettings" />);
  }

  if (errorFetchingAppInfo) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="Error loading Arrow Flight SQL OAuth Settings"
        description="Please try refreshing this page, or contact support@coefficient.io"
      />
    );
  }

  if (!appInfo) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="Could not find Arrow Flight SQL OAuth Settings for this account"
        description="It's possible that the settings have been deleted."
      />
    );
  }

  const callHeaders = appInfo.call_headers?.filter(h => h.key) ?? [];

  return renderContent(
    <Content>
      <OAuthConfiguredAlert dataSourceType="arrow_flight_sql" />
      <Form style={{ marginTop: "30px" }}>
        <FormGroup>
          <label>Host</label>
          <Input disabled value={appInfo.host} />
        </FormGroup>
        <FormGroup>
          <label>Port</label>
          <Input disabled value={appInfo.port} />
        </FormGroup>
        <FormGroup>
          <label>Client ID</label>
          <Input disabled value={appInfo.client_id} />
        </FormGroup>
        <div style={{ marginBottom: 12 }}>
          <CallHeaderLabel>Custom Call Headers</CallHeaderLabel>
          {callHeaders.length > 0 ? (
            callHeaders.map((header, idx) => (
              <div key={idx} style={{ color: COLORS.black45, marginBottom: 4 }}>
                {header.key}: {header.value}
              </div>
            ))
          ) : (
            <div style={{ color: COLORS.black45 }}>None</div>
          )}
        </div>
        <OAuthActions>
          <OAuthDeleteButton
            htmlType="button"
            onClick={() => {
              setDeleteConfirmed(false);
              setShowDeleteModal(true);
            }}
          />
        </OAuthActions>
      </Form>
      <Modal open={showDeleteModal} onCancel={() => setShowDeleteModal(false)}>
        <Typography fontWeight="bold" variant="h6" gutterBottom>
          Please Confirm
        </Typography>
        <Typography color="textSecondary">
          Are you sure you want to delete this Arrow Flight SQL OAuth configuration?
        </Typography>
        <StyledCheckbox
          onChange={() => setDeleteConfirmed(prev => !prev)}
          checked={deleteConfirmed}
        >
          Got it, please proceed
        </StyledCheckbox>
        <Footer>
          <Button
            type="text"
            disabled={deleteAppInfoMutation.isLoading}
            onClick={() => {
              setShowDeleteModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="text"
            danger
            disabled={!deleteConfirmed || deleteAppInfoMutation.isLoading}
            onClick={() => {
              if (appInfo.client_id && deleteConfirmed) {
                deleteAppInfoMutation.mutate(appInfo);
              }
            }}
          >
            Delete
          </Button>
        </Footer>
      </Modal>
    </Content>
  );
};
